import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  CloudArrowDownIcon,
  CurrencyDollarIcon,
  DatabaseIcon,
  DeviceMobileIcon,
  DotsThreeIcon,
  FilesIcon,
  GlobeIcon,
  type Icon,
  LayoutIcon,
  LockSimpleIcon,
  MagicWandIcon,
  PaletteIcon,
  ProhibitIcon,
  ShieldCheckIcon,
  StarIcon,
  TranslateIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import { motion } from "motion/react";
import { useMemo } from "react";

import { cn } from "@/utils/style";

type Feature = {
  id: string;
  icon: Icon;
  title: string;
  description: string;
};

type FeatureCardProps = Feature;

const getFeatures = (): Feature[] => [
  {
    id: "free",
    icon: CurrencyDollarIcon,
    title: t`100% Free`,
    description: t`Completely free, forever. No hidden costs, no premium tiers.`,
  },
  {
    id: "easy-start",
    icon: StarIcon,
    title: t`Ready in Minutes`,
    description: t`Pick a template, fill in your details, and your professional resume is ready to share.`,
  },
  {
    id: "no-ads",
    icon: ProhibitIcon,
    title: t`No Ads, No Tracking`,
    description: t`A clean, distraction-free experience. We never track you or show advertisements.`,
  },
  {
    id: "data-security",
    icon: DatabaseIcon,
    title: t`Your Data Stays Private`,
    description: t`Your personal information is secure and never shared or sold to anyone.`,
  },
  {
    id: "works-anywhere",
    icon: DeviceMobileIcon,
    title: t`Works on Any Device`,
    description: t`Build and edit your resume from your phone, tablet, or computer — anytime, anywhere.`,
  },
  {
    id: "languages",
    icon: TranslateIcon,
    title: t`20+ Languages`,
    description: t`Create your resume in Arabic, English, Hindi, Urdu, and many more languages.`,
  },
  {
    id: "auth",
    icon: UserCircleIcon,
    title: t`Easy Sign-In`,
    description: t`Get started in seconds with Google or email. No complicated setup required.`,
  },
  {
    id: "secure-account",
    icon: ShieldCheckIcon,
    title: t`Account Security`,
    description: t`Keep your account safe with passkeys and two-factor authentication.`,
  },
  {
    id: "unlimited-resumes",
    icon: FilesIcon,
    title: t`Unlimited Resumes`,
    description: t`Create different resumes for different jobs — no limits, ever.`,
  },
  {
    id: "design",
    icon: PaletteIcon,
    title: t`Fully Customizable`,
    description: t`Change colors, fonts, and layouts with a few clicks. No design skills needed.`,
  },
  {
    id: "ai-powered",
    icon: MagicWandIcon,
    title: t`AI-Powered Styling`,
    description: t`Let AI help you customize your resume's look and feel to match your style perfectly.`,
  },
  {
    id: "templates",
    icon: LayoutIcon,
    title: t`12+ Professional Templates`,
    description: t`Beautiful, job-ready templates designed for every industry and role.`,
  },
  {
    id: "public",
    icon: GlobeIcon,
    title: t`Shareable Links`,
    description: t`Share your resume with a simple link — perfect for sending to recruiters.`,
  },
  {
    id: "password-protection",
    icon: LockSimpleIcon,
    title: t`Password Protection`,
    description: t`Control who sees your resume by adding a password to your shared link.`,
  },
  {
    id: "export",
    icon: CloudArrowDownIcon,
    title: t`Export as PDF`,
    description: t`Download your resume as a professional PDF, ready to email or print.`,
  },
  {
    id: "more",
    icon: DotsThreeIcon,
    title: t`And much more...`,
    description: t`We're always adding new features to help you land your next job. Check back often!`,
  },
];

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <motion.div
      className={cn(
        "group relative flex min-h-48 flex-col gap-4 overflow-hidden border-b bg-background p-6 transition-[background-color] duration-300 will-change-[transform,opacity]",
        "not-nth-[2n]:border-r xl:not-nth-[4n]:border-r",
        "hover:bg-secondary/30",
      )}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      whileHover={{ y: -3, scale: 1.01 }}
      whileTap={{ scale: 0.995 }}
    >
      {/* Hover gradient overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />

      {/* Icon */}
      <div aria-hidden="true" className="relative">
        <div className="inline-flex rounded-md bg-primary/5 p-2.5 text-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
          <Icon size={24} weight="thin" />
        </div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col gap-y-1.5">
        <h3 className="text-base font-semibold tracking-tight transition-colors group-hover:text-primary">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
    </motion.div>
  );
}

export function Features() {
  const features = useMemo(() => getFeatures(), []);

  return (
    <section id="features">
      {/* Header */}
      <motion.div
        className="space-y-4 p-4 will-change-[transform,opacity] md:p-8 xl:py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45 }}
      >
        <h2 className="text-2xl font-semibold tracking-tight md:text-4xl xl:text-5xl">
          <Trans>Features</Trans>
        </h2>

        <p className="max-w-2xl leading-relaxed text-muted-foreground">
          <Trans>
            Everything you need to create, customize, and share professional resumes. Built with your privacy in mind,
            and completely free forever.
          </Trans>
        </p>
      </motion.div>

      {/* Features Grid */}
      <div className="xs:grid-cols-2 grid grid-cols-1 border-t xl:grid-cols-4">
        {features.map((feature) => (
          <FeatureCard key={feature.id} {...feature} />
        ))}
      </div>
    </section>
  );
}
